import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const Navbar = ({ isDark, setIsDark }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setMobileMenuOpen(false), [location]);

  const navLinks = [
    {
      name: 'Work With Me',
      path: '/work-with-me'
    },
    {
      name: 'Services',
      path: '/fractional-cmo',
      submenu: [
        { name: 'Fractional CMO', path: '/fractional-cmo' },
        { name: 'The MarTech Toolbox', path: '/toolbox' },
        { name: 'Case Studies', path: '/case-studies' }
      ]
    },
    {
      name: 'Resources',
      path: '/resources',
      submenu: [
        { name: 'The Tool Trap™', path: '/the-tool-trap' },
        { name: 'Downloads & Templates', path: '/resources/downloads' },
        { name: 'Press', path: '/press' }
      ]
    },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 dark:bg-brand-dark/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-6 group ml-2">
          {/* Refined Logo: Smaller circle, Thicker Rings */}
          <div className="shrink-0 relative w-12 h-12 flex items-center justify-center translate-y-3">
            {/* Outer Target Ring - Thicker */}
            <div className="absolute inset-[-10px] border-2 border-brand-red/20 dark:border-brand-cyan/20 rounded-full group-hover:scale-110 transition-transform duration-500"></div>
            {/* Inner Target Ring - Thicker */}
            <div className="absolute inset-[-5px] border-2 border-brand-red/40 dark:border-brand-cyan/40 rounded-full group-hover:scale-105 transition-transform duration-500"></div>
            
            {/* Bullseye Circle - Smaller */}
            <div className="absolute inset-0 bg-brand-red rounded-full group-hover:bg-brand-cyan transition-colors z-0 shadow-lg"></div>
            
            {/* Logo Image */}
            <img 
              src="https://media-manager-c.questera.ai/greta-media/fb12f51ee5b6b5b04fc36e6e38322fdbe9f21a5255319993208ee42085da62af087180258d5c6d79d895c821bb9cc720/images/aW1hZ2UvcG5n/f8ab48ab4bf85403ccf7fcbba8dbb012.png" 
              alt="Julie Adams Logo" 
              className="relative z-20 w-[180%] h-[180%] max-w-none object-contain drop-shadow-xl transition-transform group-hover:scale-110 group-hover:-rotate-3" 
            />
          </div>
          <span className={`font-heading tracking-[0.3em] uppercase text-xs font-black text-slate-900 dark:text-white ${isScrolled ? 'opacity-100' : 'opacity-0 md:opacity-100'} transition-opacity flex flex-col items-start justify-center`}>
            <span>Julie Adams, MSDM</span>
            <span className="font-normal opacity-70 block text-[10px] tracking-[0.1em] mt-0.5">Marketing Technology Orchestrator</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <div key={link.name} className="relative group">
              <Link
                to={link.path}
                className={`text-[10px] uppercase tracking-[0.2em] font-bold transition-colors hover:text-brand-red dark:hover:text-brand-cyan ${location.pathname === link.path ? 'text-brand-red dark:text-brand-cyan border-b-2 border-brand-red dark:border-brand-cyan pb-1' : 'text-slate-500 dark:text-slate-400'}`}
              >
                {link.name}
              </Link>
              {link.submenu && (
                <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-brand-darker border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                  {link.submenu.map((sublink) => (
                    <Link
                      key={sublink.name}
                      to={sublink.path}
                      className="block px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 dark:text-slate-400 hover:text-brand-red dark:hover:text-brand-cyan hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                    >
                      {sublink.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="w-px h-4 bg-slate-300 dark:bg-slate-700 mx-1"></div>
          <button 
            onClick={() => setIsDark(!isDark)} 
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-brand-red dark:hover:text-brand-cyan transition-colors"
            title="Toggle Theme"
          >
            <SafeIcon name={isDark ? "Sun" : "Moon"} className="w-4 h-4" />
          </button>
        </nav>

        <div className="flex items-center gap-4 lg:hidden">
          <button onClick={() => setIsDark(!isDark)} className="p-2 text-slate-500 dark:text-slate-400">
            <SafeIcon name={isDark ? "Sun" : "Moon"} className="w-5 h-5" />
          </button>
          <button aria-label="Open Menu" className="text-slate-900 dark:text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <SafeIcon name={mobileMenuOpen ? "X" : "Menu"} className="w-6 h-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white dark:bg-brand-dark border-t border-slate-100 dark:border-slate-800 px-6 py-4 shadow-lg absolute w-full"
          >
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <div key={link.name} className="flex flex-col">
                  <Link
                    to={link.path}
                    className="text-[10px] uppercase tracking-widest font-bold py-2 text-slate-600 dark:text-slate-400"
                  >
                    {link.name}
                  </Link>
                  {link.submenu && (
                    <div className="pl-4 border-l border-slate-200 dark:border-slate-700 ml-2 mt-1 flex flex-col gap-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.name}
                          to={sublink.path}
                          className="text-[10px] uppercase tracking-widest font-bold py-1 text-slate-500 dark:text-slate-500 hover:text-brand-red dark:hover:text-brand-cyan transition-colors"
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-2"></div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;